import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';

import { urlEndPoints } from '../config/end-points';
import { activityData } from '../interfaces/statop.interfaces';

@Injectable({
  providedIn: 'root',
})
export class StatopDal {
  constructor(private readonly httpClient: HttpClient) {}

  // First Row

  getOverallStats(
    fromDate: string,
    toDate: string,
    lastPreviousDates?: boolean,
    specialties?: string[],
    rooms?: string[],
    hospitalId?: string,
    deleteCache?: boolean,
    getCachedStatsOnly?: boolean
  ) {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getOverallStats?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&lastPreviousDates=' +
        lastPreviousDates +
        '&hospitalId=' +
        hospitalId +
        '&deleteCache=' +
        deleteCache +
        (specialties ? '&specialtiesFilter=' + specialties.join(',') : '') +
        (rooms ? '&roomsFilter=' + rooms.join(',') : '') +
        (getCachedStatsOnly ? '&getCachedStatsOnly=true' : '')
    );
  }

  getStatsPerRoom(
    fromDate: string,
    toDate: string,
    roomId: string,
    specialties?: string[],
    lastPreviousDates?: boolean,
    deleteCache?: boolean
  ) {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getOverallStats?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&lastPreviousDates=' +
        lastPreviousDates +
        '&deleteCache=' +
        deleteCache +
        '&roomsFilter=' +
        roomId +
        (specialties ? '&specialtiesFilter=' + specialties.join(',') : '')
    );
  }

  getStatsPerSpecialty(
    fromDate: string,
    toDate: string,
    specialtyId: string,
    isGeneralSurgeon: string,
    rooms?: string[],
    lastPreviousDates?: boolean,
    emergencies?: string,
    hospitalId?: string,
    deleteCache?: boolean
  ) {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getOverallStats?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&isGeneralSurgeon=' +
        isGeneralSurgeon +
        '&lastPreviousDates=' +
        lastPreviousDates +
        '&emergencies=' +
        emergencies +
        '&hospitalId=' +
        hospitalId +
        '&deleteCache=' +
        deleteCache +
        '&specialtiesFilter=' +
        specialtyId +
        (rooms ? '&roomsFilter=' + rooms.join(',') : '')
    );
  }

  getStatsPerSurgeon(
    fromDate: string,
    toDate: string,
    surgeonId: string,
    roomId?: string,
    lastPreviousDates?: boolean,
    hospitalVacationsStartTime?: string,
    hospitalVacationsEndTime?: string,
    deleteCache?: boolean
  ) {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getOverallStats?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&surgeonId=' +
        surgeonId +
        '&lastPreviousDates=' +
        lastPreviousDates +
        '&hospitalVacationsStartTime=' +
        hospitalVacationsStartTime +
        '&hospitalVacationsEndTime=' +
        hospitalVacationsEndTime +
        '&deleteCache=' +
        deleteCache +
        (roomId ? '&roomsFilter=' + roomId : '')
    );
  }

  getOverallStatsBySurgeon(
    fromDate: string,
    toDate: string,
    hospitalId: string,
    lastPreviousDates?: boolean,
    deleteCache?: boolean,
    getCachedStatsOnly?: boolean
  ) {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getOverallStatsBySurgeon?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&hospitalId=' +
        hospitalId +
        '&lastPreviousDates=' +
        lastPreviousDates +
        '&deleteCache=' +
        deleteCache +
        '&getCachedStatsOnly=' +
        getCachedStatsOnly
    );
  }

  getOverallStatsGroupedByType(
    fromDate: string,
    toDate: string,
    hospitalVacationsStartTime: string,
    hospitalVacationsEndTime: string,
    hospitalId: string,
    currentView: string,
    createTask: boolean,
    taskName: string,
    data: any,
    periodicity?: string,
    isComparisonEnabled?: boolean
  ): Observable<HttpResponse<Blob>> {
    const paramsObj: Record<string, string> = {
      fromDate,
      toDate,
      hospitalVacationsStartTime,
      hospitalVacationsEndTime,
      hospitalId,
      currentView,
      createTask: createTask ? 'true' : 'false',
      taskName,
      isComparisonEnabled: isComparisonEnabled ? 'true' : 'false',
    };

    if (periodicity) {
      paramsObj['periodicity'] = periodicity;
    }

    const params = new HttpParams({ fromObject: paramsObj });

    return this.httpClient.post(
      urlEndPoints.statop + 'getOverallStatsGroupedByType',
      { data },
      {
        params,
        responseType: 'blob',
        observe: 'response',
      }
    );
  }

  getActivityData(
    fromDate: string,
    toDate: string,
    profilesType: string | undefined,
    hospitalId: string,
    surgeonId: string | undefined,
    roomId: string | undefined,
    specialtyId: string | undefined,
    limit: string,
    hospitalVacationsStartTime: string,
    hospitalVacationsEndTime: string
  ): Observable<activityData> {
    return this.httpClient.get<activityData>(
      urlEndPoints.statop +
        'activity?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&profilesType=' +
        profilesType +
        '&hospitalId=' +
        hospitalId +
        '&surgeonId=' +
        surgeonId +
        '&roomId=' +
        roomId +
        '&specialtyId=' +
        specialtyId +
        '&startTime=' +
        hospitalVacationsStartTime +
        '&endTime=' +
        hospitalVacationsEndTime +
        '&limit=' +
        limit
    );
  }

  getRoomAnalysisData(
    fromDate: string,
    toDate: string,
    roomId: string,
    hospitalVacationsStartTime: string,
    hospitalVacationsEndTime: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'roomAnalysis?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&roomId=' +
        roomId +
        '&startTime=' +
        hospitalVacationsStartTime +
        '&endTime=' +
        hospitalVacationsEndTime
    );
  }

  getSpecialtyAnalysisData(
    fromDate: string,
    toDate: string,
    specialtyId: string,
    hospitalVacationsStartTime: string,
    hospitalVacationsEndTime: string,
    isGeneralSurgeon?: boolean
  ): Observable<any> {
    if (isGeneralSurgeon) {
      return this.httpClient.get<any>(
        urlEndPoints.statop +
          'specialtyAnalysis?fromDate=' +
          fromDate +
          '&toDate=' +
          toDate +
          '&specialtyId=' +
          specialtyId +
          '&isGeneralSurgeon=' +
          isGeneralSurgeon +
          '&startTime=' +
          hospitalVacationsStartTime +
          '&endTime=' +
          hospitalVacationsEndTime
      );
    } else {
      return this.httpClient.get<any>(
        urlEndPoints.statop +
          'specialtyAnalysis?fromDate=' +
          fromDate +
          '&toDate=' +
          toDate +
          '&specialtyId=' +
          specialtyId +
          '&startTime=' +
          hospitalVacationsStartTime +
          '&endTime=' +
          hospitalVacationsEndTime
      );
    }
  }

  getStatOpInterventionsTotal(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'totalInterventions?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getStatOpInterventionsProgress(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.statop + 'interventions?fromDate=' + startDate + '&toDate=' + endDate);
  }

  getStatOpInterventionsPerDay(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'interventionsPerDay?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  // Second Row

  getStatOpInterventionsPerSpecialty(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'interventionPerSpecialty?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getStatOpInterventionsTopSpecialty(startDate: string, endDate: string, limit: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'topSpecialty?fromDate=' + startDate + '&toDate=' + endDate + '&limit=' + limit
    );
  }

  getRoomData(startDate: string, endDate: string, roomId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'oneRoomStat?fromDate=' + startDate + '&toDate=' + endDate + '&roomId=' + roomId
    );
  }

  getRoomTop(startDate: string, endDate: string, roomId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'HeaderTop?fromDate=' + startDate + '&toDate=' + endDate + '&roomId=' + roomId
    );
  }

  getRatesHeader(startDate: string, endDate: string, roomId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'ratesHeader?fromDate=' + startDate + '&toDate=' + endDate + '&roomId=' + roomId
    );
  }

  getRatesHeaderSpecialty(startDate: string, endDate: string, specialtyId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'ratesHeaderSpecialty?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&specialtyId=' +
        specialtyId
    );
  }

  getSpecialtyTop(startDate: string, endDate: string, specialtyId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'HeaderTopBySpecialty/?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&specialtyId=' +
        specialtyId
    );
  }

  // getRoomEffectivenessRatePerDay(startDate: string, endDate: string, roomId: string): Observable<any> {
  //   return this.httpClient.get<any>(
  //     urlEndPoints.statop +
  //       'roomeEffectivenessRatePerDay?fromDate=' +
  //       startDate +
  //       '&toDate=' +
  //       endDate +
  //       '&roomId=' +
  //       roomId
  //   );
  // }

  // getRoomEffectivenessRate(startDate: string, endDate: string, roomId: string): Observable<any> {
  //   return this.httpClient.get<any>(
  //     urlEndPoints.statop + 'roomEffectivenessRate?fromDate=' + startDate + '&toDate=' + endDate + '&roomId=' + roomId
  //   );
  // }

  getRoomsBubblesData(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.statop + 'roomsBubles?fromDate=' + startDate + '&toDate=' + endDate);
  }

  getStatOpBoxPlot(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'boxPlotSpecialty?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  //////////////////
  // One Surgeon  //
  //////////////////

  getOneSurgeonFirstGeneralData(
    startDate: string,
    endDate: string,
    surgeonId: string,
    hospital?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'surgeonHeader?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        (hospital ? '&hospital=' + hospital : '')
    );
  }

  getOneSurgeonStatOpInterventionsTotal(
    startDate: string,
    endDate: string,
    surgeonId: string,
    roomId?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'surgeonTotalInterventions?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&roomId=' +
        roomId
    );
  }

  getOneSurgeonStatOpInterventionsTotalPerRoom(
    startDate: string,
    endDate: string,
    surgeonId: string,
    roomId: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      `${urlEndPoints.statop}surgeonTotalInterventionsPerRoom?fromDate=${startDate}&toDate=${endDate}&surgeonId=${surgeonId}&roomId=${roomId}`
    );
  }

  getOneSurgeonStatOpInterventionsProgress(startDate: string, endDate: string, surgeonId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'surgeonInterventions?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId
    );
  }

  getOneSurgeonStatOpInterventionsPerDay(startDate: string, endDate: string, surgeonId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'surgeonInterventionsPerDay?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId
    );
  }

  // getOneSurgeonEffectivenessRate(startDate: string, endDate: string, surgeonId: string): Observable<any> {
  //   return this.httpClient.get<any>(
  //     urlEndPoints.statop +
  //       'surgeonEffectivenessRate?fromDate=' +
  //       startDate +
  //       '&toDate=' +
  //       endDate +
  //       '&surgeonId=' +
  //       surgeonId
  //   );
  // }

  // getOneSurgeonEffectivenessRatePerDay(startDate: string, endDate: string, surgeonId: string): Observable<any> {
  //   return this.httpClient.get<any>(
  //     urlEndPoints.statop +
  //       'surgeonEffectivenessRatePerDay?fromDate=' +
  //       startDate +
  //       '&toDate=' +
  //       endDate +
  //       '&surgeonId=' +
  //       surgeonId
  //   );
  // }

  getAdditionRate(fromDate: string, toDate: string, surgeonId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getLateDoctorAgendaPercentage/?surgeonId=' +
        surgeonId +
        '&fromDate=' +
        fromDate +
        '&toDate=' +
        toDate
    );
  }

  getOvertakingRate(fromDate: string, toDate: string, surgeonId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'roomsOverflowRateBySurgeon/?surgeonId=' +
        surgeonId +
        '&fromDate=' +
        fromDate +
        '&toDate=' +
        toDate
    );
  }

  getCountsPerReasons(hospitalId: string, startDate: string, endDate: string, profilesType: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'countsPerReasons?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&profilesType=' +
        profilesType +
        '&hospitalId=' +
        hospitalId
    );
  }

  getProfilesByReasonAndDate(
    hospitalId: string,
    date: string,
    reasonId: string,
    profilesType: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'profiles?date=' +
        date +
        '&reason=' +
        reasonId +
        '&profilesType=' +
        profilesType +
        '&hospitalId=' +
        hospitalId
    );
  }

  getStatOpEffectifPerSeniority(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'effectifPerSeniority?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getStatOpEffectifTotal(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.statop + 'totalEffectif?fromDate=' + startDate + '&toDate=' + endDate);
  }

  getStatOpResidencySeniorityDistribution(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'seniorityResidencyDistribution?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getStatOpNeedsPerSpecialty(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'needsPerSpecialty?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getStatOpAvailability(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'availabilities?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getTopThreeNumberOfGuards(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.statop + 'top3/guards?fromDate=' + startDate + '&toDate=' + endDate);
  }

  getStatOpAbsencePerReason(startDate: string, endDate: string, profile?: string): Observable<any> {
    const url = profile
      ? profile + '?fromDate=' + startDate + '&toDate=' + endDate
      : '?fromDate=' + startDate + '&toDate=' + endDate;
    return this.httpClient.get<any>(urlEndPoints.statop + 'details/absences/' + url);
  }

  getStatOpExtracliniqueProfiles(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'to/prioritize/for/extraclinique?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getStatOpGeneral(startDate: string, endDate: string, type: string, specialty: string | undefined): Observable<any> {
    let query = '';
    if (type) {
      query += '&type=' + type;
    }
    if (specialty) {
      query += '&specialty=' + specialty;
    }

    return this.httpClient.get<any>(
      urlEndPoints.statop + 'general?fromDate=' + startDate + '&toDate=' + endDate + query
    );
  }

  getStatOpDistributionPerType(startDate: string, endDate: string, profileId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'work/distribution/' + profileId + '?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getStatOpNumberOfGuards(startDate: string, endDate: string, profileId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'total/gardes/' + profileId + '?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getStatOpGuardDsitribution(startDate: string, endDate: string, profileId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'distribution/gardes/' + profileId + '?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getStatOpWeekendGuardDsitribution(startDate: string, endDate: string, profileId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'distribution/weekendGardes/' + profileId + '?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getSurgeonsOverflowRate(startDate: string, endDate: string, hasToValidate?: boolean): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getSurgeonOverflowRate?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&hasToValidate=' +
        hasToValidate
    );
  }

  getRatesPerDateAndRoom(startDate: string, endDate: string, hospitalId: string): Observable<any> {
    return this.httpClient.post<any>(
      urlEndPoints.statop +
        'ratesPerDateAndRoom?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&hospitalId=' +
        hospitalId,
      null
    );
  }

  /* ***************************** */

  getTVO(
    startDate: string,
    endDate: string,
    surgeonId?: string,
    roomId?: string,
    specialtyId?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getTVO?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&roomId=' +
        roomId +
        '&specialtyId=' +
        specialtyId
    );
  }

  getTROS(
    startDate: string,
    endDate: string,
    surgeonId?: string,
    roomId?: string,
    specialtyId?: string,
    withoutUrgences?: boolean
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getTROS?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&roomId=' +
        roomId +
        '&specialtyId=' +
        specialtyId +
        '&withoutUrgences=' +
        withoutUrgences
    );
  }

  getTROSAllRoomsBySurgeon(
    startDate: string,
    endDate: string,
    surgeonId?: string,
    withoutUrgences?: boolean
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'surgeonAllRoomsData?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&withoutUrgences=' +
        withoutUrgences
    );
  }

  getAverageLateStartRate(
    startDate: string,
    endDate: string,
    surgeonId?: string,
    roomId?: string,
    specialtyId?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getAverageLateStartRate?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&roomId=' +
        roomId +
        '&specialtyId=' +
        specialtyId
    );
  }

  getAverageEarlyEndRate(
    startDate: string,
    endDate: string,
    surgeonId?: string,
    roomId?: string,
    specialtyId?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getAverageEarlyEndRate?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&roomId=' +
        roomId +
        '&specialtyId=' +
        specialtyId
    );
  }

  getIsAmbuRate(
    startDate: string,
    endDate: string,
    surgeonId?: string,
    roomId?: string,
    specialtyId?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getIsAmbuRate?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&roomId=' +
        roomId +
        '&specialtyId=' +
        specialtyId
    );
  }

  getIsUrgencesRate(
    startDate: string,
    endDate: string,
    surgeonId?: string,
    roomId?: string,
    specialtyId?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getIsUrgencesRate?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&roomId=' +
        roomId +
        '&specialtyId=' +
        specialtyId
    );
  }

  getTimeBetweenInterventions(
    startDate: string,
    endDate: string,
    surgeonId?: string,
    roomId?: string,
    specialtyId?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getTimeBetweenInterventions?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&roomId=' +
        roomId +
        '&specialtyId=' +
        specialtyId
    );
  }

  getCleaningTime(
    startDate: string,
    endDate: string,
    surgeonId?: string,
    roomId?: string,
    specialtyId?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getCleaningTime?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&roomId=' +
        roomId +
        '&specialtyId=' +
        specialtyId
    );
  }

  getRoomHours(startDate: string, endDate: string, roomId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'getRoomHours?fromDate=' + startDate + '&toDate=' + endDate + '&roomId=' + roomId
    );
  }

  getEquityPerDate(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(urlEndPoints.statop + 'equityPerDate?fromDate=' + startDate + '&toDate=' + endDate);
  }

  getGuardEquityPerDate(startDate: string, endDate: string, position: string, guardId: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'guardEquityPerDate?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&position=' +
        position +
        '&guardId=' +
        guardId
    );
  }

  getCodesCountData(
    startDate: string,
    endDate: string,
    position: string,
    guardId: string,
    period: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'codesCountPerProfile?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&position=' +
        position +
        '&guardId=' +
        guardId +
        '&period=' +
        period
    );
  }

  getInterventions(fromDate: string, toDate: string, hospitalId: any): Observable<any[] | undefined> {
    return this.httpClient.get<any[] | undefined>(
      urlEndPoints.statop + 'getInterventions?fromDate=' + fromDate + '&toDate=' + toDate + '&hospitalId=' + hospitalId
    );
  }

  getActDetails(act: string, fromDate: string, toDate: string, hospitalId: any): Observable<any[] | undefined> {
    return this.httpClient.get<any[] | undefined>(
      urlEndPoints.statop +
        'getActDetails?act=' +
        act +
        '&fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&hospitalId=' +
        hospitalId
    );
  }

  getMatchingProgrammedActs(
    act: string,
    fromDate: string,
    toDate: string,
    hospitalId: any
  ): Observable<any[] | undefined> {
    return this.httpClient.get<any[] | undefined>(
      urlEndPoints.statop +
        'getMatchingProgrammedActs?act=' +
        act +
        '&fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&hospitalId=' +
        hospitalId
    );
  }

  getTopInterventionsByCA(
    surgeonId: string,
    specialtyId: string,
    startDate: string,
    endDate: string,
    isAnesth: boolean
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'topInterventionsByCA?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&isAnesth=' +
        isAnesth +
        '&specialtyId=' +
        specialtyId +
        '&surgeonId=' +
        surgeonId
    );
  }

  getTotalCACosts(startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop + 'getTotalCACosts?fromDate=' + startDate + '&toDate=' + endDate
    );
  }

  getVacationsCACosts(startDate: string, endDate: string, isAnesth: boolean): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getVacationsCACosts?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&isAnesth==' +
        isAnesth
    );
  }

  getCA(surgeonId: string, specialtyId: string, startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getCA?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&specialtyId=' +
        specialtyId
    );
  }

  getCACumul(surgeonId: string, specialtyId: string, startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getCumulCA?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&specialtyId=' +
        specialtyId
    );
  }

  getTotalCACostsBySpe(surgeonId: string, specialtyId: string, startDate: string, endDate: string): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getTotalCACostsBySpe?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&specialtyId=' +
        specialtyId
    );
  }

  getTotalCostsByFormation(
    surgeonId: string,
    specialtyId: string,
    startDate: string,
    endDate: string,
    residency: string,
    isAnesth: boolean
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'getTotalCostsByFormation?fromDate=' +
        startDate +
        '&toDate=' +
        endDate +
        '&surgeonId=' +
        surgeonId +
        '&specialtyId=' +
        specialtyId +
        '&residency=' +
        residency +
        '&isAnesth=' +
        isAnesth
    );
  }

  getSurgeonOpeningsStats(
    fromDate: string,
    toDate: string,
    deleteCache: boolean,
    period: string,
    previousDates: boolean,
    surgeon?: string,
    rooms?: string[],
    specialties?: string[],
    day?: string,
    week?: string
  ): Observable<any> {
    return this.httpClient.get<any>(
      urlEndPoints.statop +
        'surgeonOpeningsStats?fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&deleteCache=' +
        deleteCache +
        '&period=' +
        period +
        '&previousDates=' +
        previousDates +
        (surgeon ? '&surgeon=' + surgeon : '') +
        (rooms ? '&rooms=' + rooms.join(',') : '') +
        (specialties ? '&specialties=' + specialties.join(',') : '') +
        (day ? '&day=' + day : '') +
        (week ? '&week=' + week : '')
    );
  }

  getHomepageStatistics(date: string, hospitals: string, rooms?: string): Observable<any> {
    return this.httpClient.get<any>(
      `${urlEndPoints.statop}homepageStatistics?date=${date}&hospitals=${hospitals}${rooms ? `&rooms=${rooms}` : ''}`
    );
  }

  downloadExport(taskId: string): Observable<HttpResponse<Blob>> {
    return this.httpClient.get(urlEndPoints.statop + `downloadExport/${taskId}`, {
      responseType: 'blob',
      observe: 'response',
    });
  }
}
